import {
	createContext,
	type ReactNode,
	type Ref,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'

import FocusTrap from 'focus-trap-react'
import { capitalize } from 'lodash'

import { subscribeEventTarget } from '@subaio/utils'

import { useEvent } from '../../useEvent'
import { injectAnimationRenderProps, useKeyedAnimation } from '../../useKeyedAnimation'
import { useScrollParentRef } from '../../useScrollParentRef'
import { type DefaultContentRender, normalizeDefaultContent } from '../DefaultContent'
import type { ModalSetup } from '../ModalSetup'
import type { ModalShadowRender } from '../ModalShadow'
import { AbsoluteContainer } from './AbsoluteContainer'
import { FixedContainer } from './FixedContainer'
import type { ModalHandle } from './ModalHandle'

type Props<T extends Record<string, ModalSetup<any>>> = {
	/** Should modals be placed in the nearest parent relative context, or in fixed context */
	mode?: 'fixed' | 'absolute'

	children?: ReactNode
	ref?: Ref<ModalContext<T>>
}

type Options<T extends Record<string, ModalSetup<any>>> = {
	types: T
	renderShadow: ModalShadowRender
}

type ModalReturn = Pick<ModalHandle, 'onClose' | 'onUpdate'>

export type ModalRenderer<T extends ModalSetup<any>> =
	T extends ModalSetup<infer TRender> ? (content: TRender) => ModalReturn : never

type ModalContext<T extends Record<string, ModalSetup<any>>> = {
	[TKey in keyof T]: ModalRenderer<T[TKey]>
}

type ModalStaticName<T extends string> = T extends `show${infer Name}`
	? `Modal${Name}Static`
	: `Modal${Capitalize<T>}Static`
type ModalStatic<T extends ModalSetup<any>> =
	T extends ModalSetup<infer TRender>
		? TRender extends DefaultContentRender
			? (props: { render: TRender; isVisible?: boolean }) => null
			: never
		: never

type ModalCreateReturn<T extends Record<string, ModalSetup<any>>> = {
	ModalProvider: (props: Props<T>) => React.JSX.Element
	useModal: () => ModalContext<T>
	/**
	 * Expose each of the added modal types as a static component.
	 *
	 * The static component takes a render function, and will simply show the modal as long as the component is mounted.
	 */
	staticModals: {
		[TKey in keyof T & string as ModalStaticName<TKey>]: ModalStatic<T[TKey]>
	}
}

export const modalCreate = <T extends Record<string, ModalSetup<any>>>({
	types,
	renderShadow,
}: Options<T>): ModalCreateReturn<T> => {
	const context = createContext<ModalContext<T> | undefined>(undefined)

	const ModalProvider = ({ mode = 'fixed', children, ref }: Props<T>) => {
		const [modals, setModals] = useState<Array<ModalHandle>>([])
		const idRef = useRef(0)
		const scrollParentRef = useScrollParentRef()
		const keyedAnimation = useKeyedAnimation({
			// Duration is set per modal
			duration: 0,
			animateInitial: true,
		})

		const Container = mode === 'fixed' ? FixedContainer : AbsoluteContainer

		const contextValue = useMemo(
			() =>
				Object.fromEntries(
					Object.entries(types).map(([name, getModal]) => [
						name,
						(content: Function) => {
							const id = String(idRef.current++)

							const closeHandlers = [
								() => {
									setModals((current) => current.filter((other) => other.id !== id))
								},
							]

							const onClose = () => {
								closeHandlers.forEach((handler) => {
									handler()
								})
							}

							const {
								render,
								isDismissable = true,
								isFocusTrapEnabled = true,
								renderShadow,
								onClosed,
							} = getModal(content, onClose)

							if (onClosed) {
								closeHandlers.push(onClosed)
							}

							const onUpdate = (newRender: ModalHandle['render']) => {
								setModals((current) =>
									current.map((modal) =>
										modal.id === id
											? {
													...modal,
													render: newRender,
												}
											: modal,
									),
								)
							}

							setModals((current) => [
								{
									id,
									render,
									isDismissable,
									isFocusTrapEnabled,
									renderShadow,
									onClose,
									onUpdate,
								},
								...current,
							])

							return { onClose, onUpdate }
						},
					]),
				) as ModalContext<T>,
			[],
		)

		useImperativeHandle(ref, () => contextValue, [contextValue])

		const [activeModal] = modals as [ModalHandle?]

		useEffect(() => {
			if (!activeModal || !scrollParentRef.current) return

			return scrollParentRef.current.disable()
		}, [activeModal, scrollParentRef])

		useEffect(() => {
			if (!activeModal?.isDismissable) return

			return subscribeEventTarget(document.body, 'keydown', ({ code }) => {
				if (code !== 'Escape') return

				activeModal.onClose()
			})
		}, [activeModal])

		return (
			<context.Provider value={contextValue}>
				{children}
				<FocusTrap
					active={activeModal?.isFocusTrapEnabled ?? false}
					focusTrapOptions={{
						// If the user chooses to interact outside the modal (mainly applicable to desktop)
						// it is fair to assume that the user is interacting with something that is positioned above the modal
						// and mounted to the root of the document.
						allowOutsideClick: true,
						// We do not want to move the focus for the user when the modal is opened.
						initialFocus: false,
					}}
				>
					<Container ref={scrollParentRef} hasModal={Boolean(activeModal)}>
						{keyedAnimation(
							activeModal
								? [
										[
											activeModal.renderShadow ? `${activeModal.id}-shadow` : 'shadow',
											injectAnimationRenderProps(activeModal.renderShadow ?? renderShadow, {
												onClick: activeModal?.isDismissable ? activeModal?.onClose : undefined,
											}),
										],
										[activeModal.id, activeModal.render],
									]
								: [],
						)}
					</Container>
				</FocusTrap>
			</context.Provider>
		)
	}

	const useModal = () => {
		const current = useContext(context)

		if (!current) throw new Error('Modal: Cannot show modal without provider')

		return current
	}

	const staticModals = Object.fromEntries(
		Object.entries(types).map(([name, getModal]) => [
			name.startsWith('show') ? `Modal${name.substring(4)}Static` : `Modal${capitalize(name)}Static`,
			({ render, isVisible = true }: Parameters<ModalStatic<ModalSetup<DefaultContentRender>>>[0]) => {
				const { [name]: showModal } = useModal()
				const modalRef = useRef<ModalReturn>()
				const stableRender = useEvent(render)

				useEffect(() => {
					if (!isVisible) return

					const modal = showModal((props: { onClose: () => void }) => {
						const content = normalizeDefaultContent(stableRender(props))
						return { ...content, isDismissable: false, isFocusTrapEnabled: false }
					})

					modalRef.current = modal

					return () => {
						modalRef.current?.onClose()
						modalRef.current = undefined
					}
				}, [stableRender, showModal, isVisible])

				useEffect(() => {
					if (!isVisible || !modalRef.current) return

					const { render: update } = getModal(
						(props: { onClose: () => void }) => render(props),
						modalRef.current.onClose,
					)
					modalRef.current.onUpdate(update)
				}, [render, isVisible])

				return null
			},
		]),
	)

	return { ModalProvider, useModal, staticModals: staticModals as any }
}
